import { Type, Tag, Dom, Event, Loc } from 'main.core';
import 'ui.hint';
import { Icon, Outline } from 'ui.icon-set.api.core';
import { ChipDesign, ChipSize, Chip, type ChipOptions } from 'ui.system.chip';

import { InputSize, InputDesign } from './const';

import './input.css';

export type InputOptions = {
	value?: string,
	rowsQuantity?: number,
	resize?: 'none' | 'both' | 'horizontal' | 'vertical',
	label?: string,
	labelInline?: boolean,
	placeholder?: string,
	type?: string,
	error?: string,
	size?: InputSize,
	design?: InputDesign,
	icon?: ?string,
	chips?: ChipOptions[],
	center?: boolean,
	withSearch?: boolean,
	withClear?: boolean,
	dropdown?: boolean,
	clickable?: boolean,
	stretched?: boolean,
	active?: boolean,
	readonly?: boolean,
	dataTestId?: string,
	ariaLabel?: string,
	copyable?: boolean,
	required?: boolean,
	onClick?: Function,
	onFocus?: Function,
	onBlur?: Function,
	onInput?: Function,
	onClear?: Function,
	onCopy?: Function,
	onChipClick?: Function,
	onChipClear?: Function,
};

export class Input
{
	#value: string = '';
	#rows: number = 1;
	#resize: 'none' | 'both' | 'horizontal' | 'vertical' = 'both';
	#label: string = '';
	#labelInline: boolean = false;
	#placeholder: string = '';
	#type: string = 'text';
	#error: string = '';
	#size: InputSize = InputSize.Lg;
	#design: InputDesign = InputDesign.Grey;
	#icon: ?string = null;
	#chips: ChipOptions[] = [];
	#center: boolean = false;
	#withSearch: boolean = false;
	#withClear: boolean = false;
	#dropdown: boolean = false;
	#clickable: boolean = false;
	#stretched: boolean = false;
	#active: boolean = false;
	#readonly: boolean = false;
	#copyable: boolean = false;
	#required: boolean = false;
	#passwordVisible: boolean = false;
	#dataTestId: string = '';
	#ariaLabel: string = '';

	#onClick: ?Function = null;
	#onFocus: ?Function = null;
	#onBlur: ?Function = null;
	#onInput: ?Function = null;
	#onClear: ?Function = null;
	#onCopy: ?Function = null;
	#onChipClick: ?Function = null;
	#onChipClear: ?Function = null;

	#wrapper: ?HTMLElement = null;
	#labelElement: ?HTMLElement = null;
	#containerElement: ?HTMLElement = null;
	#inputElement: ?HTMLInputElement | ?HTMLTextAreaElement = null;
	#errorElement: ?HTMLElement = null;
	#iconElement: ?HTMLElement = null;
	#clearElement: ?HTMLElement = null;
	#searchElement: ?HTMLElement = null;
	#dropdownElement: ?HTMLElement = null;
	#passwordToggleElement: ?HTMLElement = null;
	#copyElement: ?HTMLElement = null;
	#requiredElement: ?HTMLElement = null;
	#chipsInstances: Chip[] = [];
	#chipElements: HTMLElement[] = [];
	#chipsContainer: ?HTMLElement = null;
	#focused: boolean = false;

	constructor(options: InputOptions = {})
	{
		this.#applyOptions(options);
	}

	#applyOptions(options: InputOptions): void
	{
		this.#value = options.value ?? '';
		this.#rows = options.rowsQuantity ?? 1;
		this.#resize = options.resize ?? 'both';
		this.#label = options.label ?? '';
		this.#labelInline = options.labelInline === true;
		this.#placeholder = options.placeholder ?? '';
		this.#type = options.type ?? 'text';
		this.#error = options.error ?? '';
		this.#size = options.size ?? InputSize.Lg;
		this.#design = options.design ?? InputDesign.Grey;
		this.#icon = options.icon ?? null;
		this.#chips = Array.isArray(options.chips) ? options.chips : [];
		this.#center = options.center === true;
		this.#withSearch = options.withSearch === true;
		this.#withClear = options.withClear === true;
		this.#dropdown = options.dropdown === true;
		this.#clickable = options.clickable === true;
		this.#stretched = options.stretched === true;
		this.#active = options.active === true;
		this.#readonly = options.readonly === true;
		this.#copyable = options.copyable === true;
		this.#required = options.required === true;
		this.#dataTestId = options.dataTestId ?? '';
		this.#ariaLabel = options.ariaLabel ?? '';

		this.#onClick = options.onClick ?? null;
		this.#onFocus = options.onFocus ?? null;
		this.#onBlur = options.onBlur ?? null;
		this.#onInput = options.onInput ?? null;
		this.#onClear = options.onClear ?? null;
		this.#onCopy = options.onCopy ?? null;
		this.#onChipClick = options.onChipClick ?? null;
		this.#onChipClear = options.onChipClear ?? null;
	}

	render(): HTMLElement
	{
		if (this.#wrapper)
		{
			return this.#wrapper;
		}

		this.#containerElement = Tag.render`
			<div class="ui-system-input-container">
				${this.#renderChips()}
				${this.#renderIcon()}
				${this.#renderInput()}
				${this.#renderPasswordToggle()}
				${this.#renderCopyButton()}
				${this.#renderSearchIcon()}
				${this.#renderClearIcon()}
				${this.#renderDropdownIcon()}
			</div>
		`;

		this.#wrapper = Tag.render`
			<div class="ui-system-input ${this.#getWrapperClasses()}">
				${this.#renderLabel()}
				${this.#containerElement}
				${this.#renderError()}
			</div>
		`;

		this.#bindEvents();

		if (this.#active && !this.#clickable)
		{
			this.focus();
		}

		return this.#wrapper;
	}

	setValue(value: string): void
	{
		this.#value = value;

		if (this.#inputElement)
		{
			this.#inputElement.value = value;
		}
	}

	getValue(): string
	{
		return this.#value;
	}

	setLabel(value: string): void
	{
		this.#label = value;

		if (this.#labelElement)
		{
			this.#labelElement.textContent = value;

			if (this.#requiredElement)
			{
				Dom.append(this.#requiredElement, this.#labelElement);
			}
		}
	}

	getLabel(): string
	{
		return this.#label;
	}

	setAriaLabel(value: string): void
	{
		this.#ariaLabel = value ?? '';

		if (this.#inputElement)
		{
			if (this.#ariaLabel)
			{
				Dom.attr(this.#inputElement, { 'aria-label': this.#ariaLabel });
			}
			else
			{
				this.#inputElement.removeAttribute('aria-label');
			}
		}
	}

	getAriaLabel(): string
	{
		return this.#ariaLabel;
	}

	setPlaceholder(value: string): void
	{
		this.#placeholder = value;

		if (this.#inputElement)
		{
			this.#inputElement.placeholder = value;
		}
	}

	getPlaceholder(): string
	{
		return this.#placeholder;
	}

	setType(value: string): void
	{
		this.#type = value;

		if (this.#inputElement)
		{
			this.#inputElement.type = value;
		}
	}

	getType(): string
	{
		return this.#type;
	}

	setError(value: string): void
	{
		this.#error = value;

		if (this.#errorElement)
		{
			this.#errorElement.textContent = value;
		}

		this.#updateClasses();
	}

	getError(): string
	{
		return this.#error;
	}

	setSize(value: string): void
	{
		this.#size = value;
		this.#updateClasses();
	}

	getSize(): string
	{
		return this.#size;
	}

	setDesign(value: string): void
	{
		this.#design = value;
		this.#updateClasses();
		this.#updateChips();

		if (this.#isDisabled())
		{
			Dom.attr(this.#inputElement, { disabled: '' });
			Dom.attr(this.#errorElement, { hidden: '' });
		}
		else
		{
			if (this.#inputElement)
			{
				this.#inputElement.removeAttribute('disabled');
			}

			if (this.#errorElement)
			{
				this.#errorElement.removeAttribute('hidden');
			}
		}
	}

	getDesign(): string
	{
		return this.#design;
	}

	setIcon(value: string): void
	{
		this.#icon = value;
		this.#updateIcon();
	}

	getIcon(): string
	{
		return this.#icon;
	}

	setWithSearch(value: boolean): void
	{
		this.#withSearch = value === true;
		this.#updateRightIconElement(this.#searchElement, this.#withSearch);
	}

	getWithSearch(): boolean
	{
		return this.#withSearch;
	}

	getWithClear(): boolean
	{
		return this.#withClear;
	}

	setWithClear(value: boolean): void
	{
		this.#withClear = value === true;
		this.#updateRightIconElement(this.#clearElement, this.#withClear);
	}

	isDropdown(): boolean
	{
		return this.#dropdown;
	}

	setDropdown(value: boolean): void
	{
		this.#dropdown = value === true;
		this.#updateRightIconElement(this.#dropdownElement, this.#dropdown);
		this.#updateClasses();
	}

	isCopyable(): boolean
	{
		return this.#copyable;
	}

	setCopyable(value: boolean = true): void
	{
		this.#copyable = value === true;

		if (this.#copyElement)
		{
			if (this.#copyable)
			{
				this.#copyElement.removeAttribute('hidden');
			}
			else
			{
				Dom.attr(this.#copyElement, { hidden: '' });
			}
		}
	}

	isFocused(): boolean
	{
		return this.#focused;
	}

	isReadonly(): boolean
	{
		return this.#readonly;
	}

	setReadonly(value: boolean): void
	{
		this.#readonly = value === true;
		this.#updateClasses();

		if (this.#inputElement)
		{
			if (this.#readonly)
			{
				Dom.attr(this.#inputElement, { readonly: '' });
			}
			else
			{
				this.#inputElement.removeAttribute('readonly');
			}
		}
	}

	setFocused(value: boolean): void
	{
		this.#focused = value === true;
		this.#updateClasses();
	}

	isLabelInline(): boolean
	{
		return this.#labelInline;
	}

	setLabelInline(value: boolean): void
	{
		this.#labelInline = value === true;

		if (this.#labelInline)
		{
			Dom.addClass(this.#labelElement, '--inline');
		}
		else
		{
			Dom.removeClass(this.#labelElement, '--inline');
		}
	}

	isRequired(): boolean
	{
		return this.#required;
	}

	setRequired(value: boolean): void
	{
		this.#required = value === true;

		if (this.#requiredElement)
		{
			if (this.#required)
			{
				this.#requiredElement.removeAttribute('hidden');
			}
			else
			{
				Dom.attr(this.#requiredElement, { hidden: '' });
			}
		}
	}

	addChip(chipOptions: ChipOptions): void
	{
		this.#chips.push(chipOptions);
		this.#updateChips();
	}

	removeChip(chipOptions: ChipOptions): void
	{
		this.#chips = this.#chips.filter((item) => item !== chipOptions);
		this.#updateChips();
	}

	removeChips(): void
	{
		this.#chips = [];
		this.#updateChips();
	}

	getChips(): Array<Chip>
	{
		return this.#chipsInstances;
	}

	#updateClasses(): void
	{
		if (!this.#wrapper)
		{
			return;
		}

		this.#wrapper.className = `ui-system-input ${this.#getWrapperClasses()}`;
	}

	#renderLabel(): HTMLElement
	{
		this.#requiredElement = Tag.render`
			<span class="ui-system-input-label-required" ${this.#required ? '' : 'hidden'}>*</span>
		`;

		this.#labelElement = Tag.render`
			<div class="ui-system-input-label ${this.#labelInline ? '--inline' : ''}">
				${this.#label ?? ''}${this.#requiredElement}
			</div>
		`;

		return this.#labelElement;
	}

	#renderChips(): HTMLElement
	{
		this.#chipsContainer = Tag.render`<div class="ui-system-input-chips"></div>`;
		this.#updateChips();

		return this.#chipsContainer;
	}

	#updateChips(): void
	{
		this.#chipElements = [];
		this.#chipsInstances = [];
		Dom.clean(this.#chipsContainer);

		if (this.#chips && this.#chips.length > 0)
		{
			this.#chips.forEach((chipOptions) => {
				const chip = new Chip({
					...chipOptions,
					size: this.#getChipSize(),
					design: this.#isDisabled() ? ChipDesign.Disabled : (chipOptions.design ?? ChipDesign.Outline),
					onClick: (event) => {
						this.#onChipClick?.(chipOptions, event);
					},
					onClear: (event) => {
						this.#onChipClear?.(chipOptions, event);
					},
				});

				const chipWrapper = Tag.render`<div class="ui-system-input-chip">${chip.render()}</div>`;
				Dom.append(chipWrapper, this.#chipsContainer);

				this.#chipsInstances.push(chip);
				this.#chipElements.push(chipWrapper);
			});
		}
	}

	#renderIcon(): HTMLElement
	{
		this.#iconElement = Tag.render`<div class="ui-system-input-icon"></div>`;
		this.#updateIcon();

		return this.#iconElement;
	}

	#updateIcon(): void
	{
		if (!this.#iconElement)
		{
			return;
		}

		if (this.#icon)
		{
			this.#iconElement.removeAttribute('hidden');
			this.#iconElement.className = `ui-system-input-icon ui-icon-set --${this.#icon}`;
		}
		else
		{
			this.#iconElement.className = 'ui-system-input-icon';
			Dom.attr(this.#iconElement, { hidden: '' });
		}
	}

	#renderPasswordToggle(): HTMLElement
	{
		const isPassword = this.#type === 'password';
		const icon = new Icon({ icon: Outline.OBSERVER });

		this.#passwordToggleElement = Tag.render`
			<button
				type="button"
				tabindex="0"
				class="ui-system-input-action-btn"
				aria-label="${Loc.getMessage('UI_SYSTEM_INPUT_SHOW_PASSWORD_ARIA')}"
				${isPassword ? '' : 'hidden'}
			>
				${icon.render()}
			</button>
		`;

		return this.#passwordToggleElement;
	}

	#renderCopyButton(): HTMLElement
	{
		const icon = new Icon({ icon: Outline.COPY });

		this.#copyElement = Tag.render`
			<button
				type="button"
				tabindex="0"
				class="ui-system-input-action-btn"
				aria-label="${Loc.getMessage('UI_SYSTEM_INPUT_COPY_TO_CLIPBOARD_ARIA')}"
				${this.#copyable ? '' : 'hidden'}
			>
				${icon.render()}
			</button>
		`;

		return this.#copyElement;
	}

	#renderSearchIcon(): HTMLElement
	{
		this.#searchElement = Tag.render`<div class="ui-system-input-cross --${Outline.SEARCH}"></div>`;
		this.#updateRightIconElement(this.#searchElement, this.#withSearch);

		return this.#searchElement;
	}

	#renderClearIcon(): HTMLElement
	{
		const icon = new Icon({ icon: Outline.CROSS_L });
		this.#clearElement = Tag.render`<div class="ui-system-input-cross --clear">${icon.render()}</div>`;
		this.#updateRightIconElement(this.#clearElement, this.#withClear);

		return this.#clearElement;
	}

	#renderDropdownIcon(): HTMLElement
	{
		this.#dropdownElement = Tag.render`
			<div class="ui-system-input-dropdown --${Outline.CHEVRON_DOWN_L}"></div>
		`;
		this.#updateRightIconElement(this.#dropdownElement, this.#dropdown);

		return this.#dropdownElement;
	}

	#updateRightIconElement(iconElement: HTMLElement, isShow: boolean): void
	{
		if (isShow)
		{
			iconElement.removeAttribute('hidden');
			Dom.addClass(iconElement, 'ui-icon-set');
		}
		else
		{
			Dom.removeClass(iconElement, 'ui-icon-set');
			Dom.attr(iconElement, { hidden: '' });
		}
	}

	#renderInput(): HTMLElement
	{
		const commonAttrs = {
			className: 'ui-system-input-value',
			placeholder: this.#placeholder,
			disabled: this.#isDisabled(),
			readonly: this.#readonly,
			type: this.#type,
			value: this.#value,
			dataTestId: this.#dataTestId,
			ariaLabel: this.#ariaLabel,
		};

		if (this.#rows > 1)
		{
			this.#inputElement = Tag.render`
				<textarea
					class="${commonAttrs.className} --multi"
					style="resize: ${this.#resize};"
					placeholder="${commonAttrs.placeholder}"
					${commonAttrs.ariaLabel ? `aria-label="${commonAttrs.ariaLabel}"` : ''}
					${commonAttrs.disabled ? 'disabled' : ''}
					${commonAttrs.readonly ? 'readonly' : ''}
					rows="${this.#rows}"
				>${commonAttrs.value}</textarea>
			`;
		}
		else
		{
			this.#inputElement = Tag.render`
				<input
					class="${commonAttrs.className}"
					style="--placeholder-length: ${this.#placeholder.length}ch;"
					placeholder="${commonAttrs.placeholder}"
					${commonAttrs.ariaLabel ? `aria-label="${commonAttrs.ariaLabel}"` : ''}
					${commonAttrs.disabled ? 'disabled' : ''}
					${commonAttrs.readonly ? 'readonly' : ''}
					type="${commonAttrs.type}"
					value="${commonAttrs.value}"
					${commonAttrs.dataTestId ? `data-test-id="${commonAttrs.dataTestId}"` : ''}
				/>
			`;
		}

		return this.#inputElement;
	}

	#renderError(): HTMLElement | ''
	{
		this.#errorElement = Tag.render`
			<div ${this.#isDisabled() ? 'hidden' : ''} class="ui-system-input-label --error" title="${this.#error}">
				${this.#error}
			</div>
		`;

		return this.#errorElement;
	}

	#bindEvents(): void
	{
		if (!this.#wrapper || !this.#containerElement)
		{
			return;
		}

		Event.bind(this.#containerElement, 'click', this.#handleContainerClick.bind(this));

		if (this.#inputElement)
		{
			Event.bind(this.#inputElement, 'input', this.#handleInput.bind(this));
			Event.bind(this.#inputElement, 'focus', this.#handleFocus.bind(this));
			Event.bind(this.#inputElement, 'blur', this.#handleBlur.bind(this));
		}

		if (this.#clearElement)
		{
			Event.bind(this.#clearElement, 'click', this.#handleClear.bind(this));
		}

		if (this.#passwordToggleElement)
		{
			Event.bind(this.#passwordToggleElement, 'click', this.#handlePasswordToggle.bind(this));
		}

		if (this.#copyElement)
		{
			Event.bind(this.#copyElement, 'click', this.#handleCopy.bind(this));
		}
	}

	#handleContainerClick(event: MouseEvent): void
	{
		if (!this.#clickable && this.#inputElement)
		{
			this.#inputElement.focus();
		}

		this.#onClick?.(event);
	}

	#handleInput(event: InputEvent): void
	{
		if (!this.#inputElement)
		{
			return;
		}

		this.#value = this.#inputElement.value;
		this.#onInput?.(event);
	}

	#handleFocus(event: FocusEvent): void
	{
		if (this.#clickable)
		{
			event.target.blur();

			return;
		}

		this.#focused = true;
		Dom.addClass(this.#wrapper, '--active');

		this.#onFocus?.(event);
	}

	#handleBlur(event: FocusEvent): void
	{
		this.#focused = false;
		if (!this.#active)
		{
			Dom.removeClass(this.#wrapper, '--active');
		}

		this.#onBlur?.(event);
	}

	#handleClear(event: MouseEvent): void
	{
		event.stopPropagation();
		this.setValue('');
		this.#onClear?.(event);
	}

	#handlePasswordToggle(event: MouseEvent): void
	{
		event.stopPropagation();
		this.#passwordVisible = !this.#passwordVisible;

		if (this.#inputElement)
		{
			this.#inputElement.type = this.#passwordVisible ? 'text' : 'password';
		}

		if (this.#passwordToggleElement)
		{
			Dom.attr(this.#passwordToggleElement, {
				'aria-label': this.#passwordVisible
					? Loc.getMessage('UI_SYSTEM_INPUT_HIDE_PASSWORD_ARIA')
					: Loc.getMessage('UI_SYSTEM_INPUT_SHOW_PASSWORD_ARIA'),
			});

			const nextIcon = this.#passwordVisible ? Outline.CROSSED_EYE : Outline.OBSERVER;
			Dom.clean(this.#passwordToggleElement);
			new Icon({ icon: nextIcon }).renderTo(this.#passwordToggleElement);
		}
	}

	#handleCopy(event: MouseEvent): void
	{
		event.stopPropagation();

		if (!this.#value)
		{
			this.#onCopy?.(event);

			return;
		}

		const showHint = () => {
			if (this.#copyElement)
			{
				BX.UI.Hint.show(this.#copyElement, Loc.getMessage('UI_SYSTEM_INPUT_COPIED'));
				setTimeout(() => {
					BX.UI.Hint.hide(this.#copyElement);
				}, 1500);
			}
		};

		if (navigator.clipboard && window.isSecureContext)
		{
			navigator.clipboard.writeText(this.#value).then(() => showHint());
		}
		else if (BX.clipboard?.copy(this.#value))
		{
			showHint();
		}

		this.#onCopy?.(event);
	}

	#getWrapperClasses(): string
	{
		return [
			`--${this.#design}`,
			`--${this.#size}`,
			this.#center ? '--center' : '',
			this.#chips.length > 0 ? '--with-chips' : '',
			this.#rows > 1 ? '--multi' : '',
			this.#clickable ? '--clickable' : '',
			this.#dropdown ? '--dropdown' : '',
			this.#stretched ? '--stretched' : '',
			(this.#active || this.#focused) ? '--active' : '',
			this.#readonly ? '--readonly' : '',
			(this.#error && !this.#isDisabled()) ? '--error' : '',
		].filter(Boolean).join(' ');
	}

	#getChipSize(): ChipSize
	{
		return {
			[InputSize.Lg]: ChipSize.Md,
			[InputSize.Md]: ChipSize.Md,
			[InputSize.Sm]: ChipSize.Xs,
		}[this.#size] ?? ChipSize.Md;
	}

	#isDisabled(): boolean
	{
		return this.#design === InputDesign.Disabled;
	}

	focus(): void
	{
		if (this.#inputElement && !this.#clickable)
		{
			this.#inputElement.focus({ preventScroll: true });

			if (!Type.isFunction(this.#inputElement.setSelectionRange))
			{
				return;
			}

			const length = this.#value.length;
			this.#inputElement.setSelectionRange(length, length);
		}
	}

	blur(): void
	{
		this.#inputElement?.blur();
	}

	destroy(): void
	{
		if (!this.#wrapper)
		{
			return;
		}

		Event.unbindAll(this.#wrapper);

		if (this.#inputElement)
		{
			Event.unbindAll(this.#inputElement);
		}

		if (this.#clearElement)
		{
			Event.unbindAll(this.#clearElement);
		}

		if (this.#passwordToggleElement)
		{
			Event.unbindAll(this.#passwordToggleElement);
		}

		if (this.#copyElement)
		{
			Event.unbindAll(this.#copyElement);
		}

		this.#chipsInstances.forEach((chip) => chip.destroy());
		this.#chipsInstances = [];
		this.#chipElements = [];

		Dom.remove(this.#wrapper);

		this.#wrapper = null;
		this.#labelElement = null;
		this.#containerElement = null;
		this.#inputElement = null;
		this.#errorElement = null;
		this.#iconElement = null;
		this.#clearElement = null;
		this.#searchElement = null;
		this.#dropdownElement = null;
		this.#passwordToggleElement = null;
		this.#copyElement = null;
		this.#requiredElement = null;
	}
}
